import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { LatteIcon, LatteButton } from '../common';
import { checkAdminAccess } from '../../utils/adminService';
import MobileHeader from './MobileHeader';

const Header = ({ title }) => {
  const { user, profile, signOut } = useAuth();
  const { resetOnboarding, startOnboarding } = useOnboarding();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  
  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAdminMenu && !event.target.closest('.relative')) {
        setShowAdminMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdminMenu]);
  
  // 관리자 권한 확인
  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        try {
          const adminStatus = await checkAdminAccess();
          setUserIsAdmin(adminStatus);
        } catch (error) {
          console.error('관리자 권한 확인 실패:', error);
          setUserIsAdmin(false);
        } finally {
          setAdminCheckLoading(false);
        }
      } else {
        setUserIsAdmin(false);
        setAdminCheckLoading(false);
      }
    };

    checkAdmin();
  }, [user]);

  const handleStartOnboarding = () => {
    const currentPath = window.location.pathname;
    
    if (currentPath === '/dashboard') {
      startOnboarding('dashboard');
    } else if (currentPath === '/consulting') {
      startOnboarding('consulting');
    } else if (currentPath.startsWith('/resume')) {
      // 이력서 페이지에서는 편집 모드 활성화 후 온보딩 시작
      startOnboarding('resume-edit');
    } else {
      // 다른 페이지에서는 대시보드로 이동 후 온보딩 시작
      navigate('/dashboard');
      setTimeout(() => startOnboarding('dashboard'), 500);
    }
  };

  // 사용자 정보
  const displayName = profile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '사용자';
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  return (
    <header className="header relative">
    <div className="header-container">
        <div className="header-left">
          <div className="header-logo">
            <LatteIcon size="lg" />
            <button 
              onClick={() => navigate('/')}
              className="header-title"
              title="메인 페이지로 이동"
            >
              Latte is
            </button>
          </div>
          {title && (
            <>
              <span className="header-separator hidden sm:inline">|</span>
              <span className="text-lg hidden sm:inline" style={{ color: 'var(--color-text-tertiary)' }}>{title}</span>
            </>
          )}
        </div>
        
        {/* 데스크톱 헤더 */}
        <div className="header-right hidden lg:flex">
          {user ? (
          <>
          {/* 이력서 목록 */}
          <button
            onClick={() => navigate('/documents')}
            className="header-nav-btn"
            title="내 이력서 목록"
            data-tour="documents-btn"
          >
            <span>이력서 목록</span>
          </button>
          
          {/* 사용자 가이드 */}
          <button
            onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
            className="header-nav-btn"
            title="사용자 가이드"
            data-tour="guide-btn"
          >
            <span>사용자 가이드</span>
          </button>
          
          {/* 온보딩 다시 보기 */}
          <button
            onClick={handleStartOnboarding}
            className="header-nav-btn"
            title="온보딩 다시 보기"
          >
            <span>온보딩 투어</span>
          </button>
          
          {/* 플랜 및 가격 */}
          <button
            onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
            className="header-nav-btn"
            title="플랜 및 가격"
          >
            <span>플랜 및 가격</span>
          </button>
          
          {/* 블로그 */}
          <button
            onClick={() => window.open('https://latteis.com/blog', '_blank', 'noopener,noreferrer')}
            className="header-nav-btn"
            title="블로그"
          >
            <span>블로그</span>
          </button>
          
          {/* 관리자 메뉴 */}
          {!adminCheckLoading && userIsAdmin && (
            <div className="relative">
              <button
                onClick={() => setShowAdminMenu(!showAdminMenu)}
                className="header-nav-btn"
                title="관리자 메뉴"
              >
                <span>관리자</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showAdminMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <button
                    onClick={() => {
                      navigate('/admin-prompts');
                      setShowAdminMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    프롬프트 관리
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin-users');
                      setShowAdminMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    사용자 관리
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin-promo-codes');
                      setShowAdminMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    프로모션 코드 관리
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin-promo-requests');
                      setShowAdminMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    프로모션 요청 관리
                  </button>
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            title="내 프로필 보기"
          >
            {avatarUrl ? (
            <img 
            src={avatarUrl} 
            alt={displayName}
            className="w-8 h-8 rounded-full object-cover"
            onError={(e) => {
            // 이미지 로드 실패 시 기본 아이콘으로 대체
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
            }}
            />
            ) : null}
            <div 
            className="w-8 h-8 bg-gray-100 rounded-full flex-center"
            style={{ display: avatarUrl ? 'none' : 'flex' }}
            >
            </div>
            <span className="text-sm font-medium hidden sm:inline max-w-[150px] truncate" style={{ color: 'var(--color-text-secondary)' }}>{displayName}</span>
          </button>
          </>
          ) : (
            /* 로그인하지 않은 사용자를 위한 버튼 */
            <>
              <button
                onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
                className="header-nav-btn"
                title="사용자 가이드"
              >
                <span>사용자 가이드</span>
              </button>
              <button
                onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
                className="header-nav-btn"
                title="플랜 및 가격"
              >
                <span>플랜 및 가격</span>
              </button>
              <button
                onClick={() => window.open('https://latteis.com/blog', '_blank', 'noopener,noreferrer')}
                className="header-nav-btn"
                title="블로그"
              >
                <span>블로그</span>
              </button>
              <LatteButton
                onClick={() => navigate('/login')}
                variant="primary"
                size="sm"
                className="ml-2"
              >
                로그인
              </LatteButton>
            </>
          )}
        </div>
        
        {/* 모바일 헤더 컴포넌트 */}
        <MobileHeader
          user={user}
          profile={profile}
          displayName={displayName}
          avatarUrl={avatarUrl}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          adminCheckLoading={adminCheckLoading}
          userIsAdmin={userIsAdmin}
          handleStartOnboarding={handleStartOnboarding}
        />
      </div>
    </header>
  );
};

export default Header;